import * as React from 'react';

import { type SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { type Property } from 'csstype';
import kebabCase from 'lodash-es/kebabCase';

import TableCell, { type CellProps, type VerticalAlignment } from '~/components/Table/TableCell';
import TableRow, { MessageRow } from '~/components/Table/TableRow';

type CustomStyleFunctionProps<T> = {
  data: T;
  rowIndex: number;
};
export interface Column<T> {
  cell: React.ComponentType<{ data: T; rowIndex?: number } & CellProps>;
  cursor?: Property.Cursor;
  // Custom styling can be provided for the cell and header
  customCellStyles?: ((props: CustomStyleFunctionProps<T>) => SerializedStyles) | SerializedStyles;
  customHeaderStyles?: (() => SerializedStyles) | SerializedStyles;
  header: Maybe<React.ReactNode | string>;
  minWidth?: string;
  noLeftPadding?: boolean;
  noPadding?: boolean;
  noRightPadding?: boolean;
  onCellClick?: (rowIndex: number) => void;
  verticalAlign?: VerticalAlignment;
  width?: string;
}

// https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout
type GridConfig = {
  columnGap?: string;
  gap?: string;
  grid?: string;
  gridArea?: string;
  gridAutoColumns?: string;
  gridAutoFlow?: string;
  gridAutoRows?: string;
  gridColumn?: string;
  gridColumnEnd?: string;
  gridRow?: string;
  gridRowEnd?: string;
  gridRowStart?: string;
  gridTemplate?: string;
  gridTemplateAreas?: string;
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  rowGap?: string;
};

interface TableProps<T> {
  autoLayout?: boolean;
  children?: React.ReactNode;
  className?: string;
  columnVerticalAlignment?: VerticalAlignment;
  columns: Column<T>[];
  data: T[];
  fixedFirstRow?: Maybe<Column<T>[]>;
  // Providing this config specifies table to be rendered with display: grid;
  grid?: GridConfig;
  // This props only use is the "You have new orders" message.
  // We could change that and remove this functionality.
  message?: Maybe<string>;
  messageTag?: string;
  onMessageClick?: () => void;
  onRowClick?: (rowIndex: number) => void;
  rowClickTag?: string;
  selectedIndexes?: number[];
}

interface ContainerProps {
  autoLayout?: boolean;
  grid?: GridConfig;
}

const Container = styled('table')<ContainerProps>`
  width: 100%;
  border-collapse: collapse;
  table-layout: ${({ autoLayout }) => (autoLayout ? 'auto' : 'fixed')};
  ${({ grid }) =>
    grid
      ? Object.entries(grid).reduce(
          (css, [key, val]) => (val ? `${css}${kebabCase(key)}:${val};` : css),
          'display:grid;',
        )
      : ''}
`;

const TableHeader = styled('thead')<{ asGrid?: boolean }>`
  ${({ asGrid }) => (asGrid ? 'display: contents;' : '')}
`;

const TableBody = styled('tbody')<{ asGrid?: boolean }>`
  ${({ asGrid }) => (asGrid ? 'display: contents;' : '')}
`;

const MessageLink = styled('a')`
  text-decoration: underline;
  cursor: pointer;
`;

const Table = <T,>(props: TableProps<T>) => {
  const {
    autoLayout,
    children,
    columns,
    data,
    fixedFirstRow,
    grid,
    message,
    messageTag,
    onMessageClick,
    onRowClick,
    rowClickTag,
    selectedIndexes,
  } = props;

  const hideHeader = React.useMemo(() => columns.every((col) => !col.header), [columns]);

  const renderCell = (column: Column<T>, data: T, cellIndex: number, rowIndex: number) => {
    const {
      cell,
      cursor,
      customCellStyles,
      noLeftPadding,
      noPadding,
      noRightPadding,
      onCellClick,
      verticalAlign,
      width,
    } = column;

    const CellComponent = cell;

    const columnAlignment = verticalAlign || props.columnVerticalAlignment || undefined;

    const cellProps: CellProps = {
      cursor,
      customStyles:
        typeof customCellStyles === 'function'
          ? customCellStyles({ data, rowIndex })
          : customCellStyles,
      noLeftPadding,
      noPadding,
      noRightPadding,
      rowIndex,
      verticalAlign: columnAlignment,
    };

    if (hideHeader) cellProps.width = width;

    return (
      <TableCell
        {...cellProps}
        asGrid={!!grid}
        key={`cell-${rowIndex}-${cellIndex}`}
        onClick={onCellClick}
      >
        <CellComponent data={data} rowIndex={rowIndex} />
      </TableCell>
    );
  };

  const header = React.useMemo(
    () => (
      <TableHeader asGrid={!!grid}>
        <TableRow asGrid={!!grid}>
          {columns.map((column: Column<T>, index) => {
            const isComponent = typeof column.header !== 'string';
            const HeaderComponent = column.header as React.ComponentType;

            return (
              <TableCell
                customStyles={
                  typeof column.customHeaderStyles === 'function'
                    ? column.customHeaderStyles()
                    : column.customHeaderStyles
                }
                isHeader
                key={`heading-${index}`}
                minWidth={column.minWidth}
                noLeftPadding={column.noLeftPadding}
                noRightPadding={column.noRightPadding}
                width={column.width}
              >
                {isComponent ? <HeaderComponent /> : column.header}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHeader>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  );

  const rows = React.useMemo(
    () =>
      data.map((d, rowIndex) => (
        <TableRow
          asGrid={!!grid}
          dataPendo={rowClickTag || ''}
          isSelected={selectedIndexes ? selectedIndexes.includes(rowIndex) : false}
          key={`row-${rowIndex}`}
          onClick={onRowClick}
          rowIndex={rowIndex}
        >
          {columns.map((column, cellIndex) => renderCell(column, d, cellIndex, rowIndex))}
        </TableRow>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, columns, selectedIndexes],
  );

  return (
    <Container autoLayout={autoLayout} className={props.className} grid={grid}>
      {!hideHeader && header}

      <TableBody asGrid={!!grid}>
        {/* Render message. Used for example for orders page "You have new orders" message */}
        {message && !grid && (
          <MessageRow data-pendo={messageTag || ''} onClick={onMessageClick}>
            <TableCell colSpan={columns.length}>
              <MessageLink>{message}</MessageLink>
            </TableCell>
          </MessageRow>
        )}

        {/* Render a fixed row. Used in batch orders table to get the select rates for all dropdown */}
        {fixedFirstRow && (
          <TableRow key={`custom-row`}>
            {(props.fixedFirstRow || []).map((column, cellIndex) =>
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              renderCell(column, null as any, cellIndex, 0),
            )}
          </TableRow>
        )}

        {rows}
      </TableBody>
      {children}
    </Container>
  );
};

export default Table;
